import React, {FC, useCallback, useEffect, useState} from 'react'
import {salesforceApiService} from '../services/salesforceApi'
import SalesforceConfigModal from './SalesforceConfigModal'
import SalesforceManageModal from './SalesforceManageModal'
import SalesforceCatalogModal from './SalesforceCatalogModal'

interface SalesforceIntegrationCardProps {}

const SalesforceIntegrationCard: FC<SalesforceIntegrationCardProps> = () => {
  const [isConfigured, setIsConfigured] = useState(false)
  const [maskedUsername, setMaskedUsername] = useState<string | null>(null)
  const [mappedAccountsCount, setMappedAccountsCount] = useState(0)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showManageModal, setShowManageModal] = useState(false)
  const [showCatalogModal, setShowCatalogModal] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true)
      const status = await salesforceApiService.getStatus()
      setIsConfigured(status.is_configured)
      setMaskedUsername(status.username)

      if (status.is_configured) {
        try {
          const mappings = await salesforceApiService.getAccountMappings()
          setMappedAccountsCount(mappings.length)
        } catch {
          setMappedAccountsCount(0)
        }
      } else {
        setMappedAccountsCount(0)
      }
    } catch {
      setIsConfigured(false)
      setMaskedUsername(null)
      setMappedAccountsCount(0)
    } finally {
      setLoadingStatus(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  return (
    <>
      <div className='card h-100 w-100 d-flex flex-column'>
        <div className='card-header border-0 pt-5'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <div className='symbol-label bg-light-info'>
                <i className='fab fa-salesforce fs-2x text-info'></i>
              </div>
            </div>
            <div className='d-flex flex-column flex-grow-1'>
              <h3 className='card-title align-items-start flex-column mb-0'>
                <span className='card-label fw-bold fs-4 mb-2'>Salesforce</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Importar Cases como tarefas
                </span>
              </h3>
            </div>
            {!loadingStatus && (
              <span
                className={`badge ${isConfigured ? 'badge-light-success' : 'badge-light-warning'}`}
              >
                {isConfigured ? 'Configurado' : 'Não configurado'}
              </span>
            )}
          </div>
        </div>

        <div className='card-body pt-0 d-flex flex-column flex-grow-1'>
          <div className='mb-6 flex-grow-1'>
            <p className='text-muted fs-7 mb-4'>
              Configure as credenciais da Connected App, mapeie Accounts do Salesforce para projetos
              do TaskRush e importe Cases. A importação automática roda a cada 15 minutos e
              considera apenas Cases criados <strong>hoje</strong> de Accounts já mapeados. Também é
              possível importar manualmente.
            </p>
            {isConfigured && (
              <div className='d-flex flex-column gap-1'>
                {maskedUsername && (
                  <p className='text-muted fs-8 mb-0'>
                    <i className='fas fa-user me-1'></i>
                    {maskedUsername}
                  </p>
                )}
                <p className='text-muted fs-8 mb-0'>
                  <i className='fas fa-link me-1'></i>
                  {mappedAccountsCount} Account(s) mapeado(s)
                </p>
              </div>
            )}
          </div>

          <div className='mt-auto d-flex flex-column gap-2'>
            <button
              type='button'
              className='btn btn-sm btn-primary w-100'
              onClick={() => setShowConfigModal(true)}
            >
              <i className='fas fa-cog me-2'></i>
              {isConfigured ? 'Configurar credenciais' : 'Configurar integração'}
            </button>
            {isConfigured && (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary w-100'
                  onClick={() => setShowManageModal(true)}
                  disabled={loadingStatus}
                >
                  <i className='fas fa-project-diagram me-2'></i>
                  Gerenciar mapeamentos e importar
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary w-100'
                  onClick={() => setShowCatalogModal(true)}
                  disabled={loadingStatus}
                >
                  <i className='fas fa-list me-2'></i>
                  Catálogo de Produtos (SLA)
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <SalesforceConfigModal
        show={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSuccess={loadStatus}
      />

      <SalesforceManageModal
        show={showManageModal}
        onClose={() => setShowManageModal(false)}
        onSuccess={loadStatus}
      />

      <SalesforceCatalogModal show={showCatalogModal} onClose={() => setShowCatalogModal(false)} />
    </>
  )
}

export default SalesforceIntegrationCard
